/**
 * Web search collector (Layer 2) — SearXNG-backed scoped web search.
 *
 * Queries a self-hosted SearXNG instance with keywords derived from
 * the investigation scope contract. Top N results are fetched and their
 * readable text extracted for the entity extraction pipeline.
 *
 * Reliability C-D: web search results are unsourced until corroborated.
 * Gracefully degrades (skips) when SearXNG is unreachable.
 */

import type { CollectionTask } from '../types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface WebSearchResult {
  rawText: string;
  sourceCount: number;
  collectorId: string;
  error?: string;
}

interface SearXNGResult {
  url: string;
  title: string;
  content?: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DEFAULT_SEARXNG_URL = 'http://localhost:8080';
const SEARCH_TIMEOUT_MS = 20_000;
const PAGE_FETCH_TIMEOUT_MS = 10_000;
const MAX_TEXT_PER_PAGE = 5000;
const MIN_TEXT_LENGTH = 200;

// ---------------------------------------------------------------------------
// Collection
// ---------------------------------------------------------------------------

/**
 * Collect content via SearXNG web search.
 *
 * Builds a search query from the collection task parameters, queries
 * SearXNG, fetches top results, and extracts readable text.
 */
export async function collectFromWebSearch(
  task: CollectionTask,
  searxngUrl: string = DEFAULT_SEARXNG_URL,
  maxResults: number = 10,
): Promise<WebSearchResult> {
  const query = buildSearchQuery(task);

  try {
    const searchUrl = `${searxngUrl}/search?q=${encodeURIComponent(query)}&format=json&engines=google,bing,duckduckgo`;
    const response = await fetch(searchUrl, {
      signal: AbortSignal.timeout(SEARCH_TIMEOUT_MS),
    });

    if (!response.ok) {
      return {
        rawText: '',
        sourceCount: 0,
        collectorId: 'web_search',
        error: `SearXNG returned ${response.status}`,
      };
    }

    const data = await response.json() as { results?: SearXNGResult[] };
    const results = (data.results ?? []).slice(0, maxResults);

    // Fetch each result page and extract text
    const texts: string[] = [];
    for (const result of results) {
      try {
        const pageResponse = await fetch(result.url, {
          signal: AbortSignal.timeout(PAGE_FETCH_TIMEOUT_MS),
          headers: { 'User-Agent': 'Sentinel-OSINT/1.0' },
        });
        if (pageResponse.ok) {
          const html = await pageResponse.text();
          const text = extractReadableText(html);
          if (text && text.length > MIN_TEXT_LENGTH) {
            texts.push(`[Source: ${result.title} | ${result.url}]\n${text.slice(0, MAX_TEXT_PER_PAGE)}`);
          }
        }
      } catch {
        // Skip failed pages
      }
    }

    return {
      rawText: texts.join('\n\n---\n\n'),
      sourceCount: texts.length,
      collectorId: 'web_search',
    };
  } catch {
    // SearXNG not reachable — graceful degradation
    console.warn('[web-search-collector] SearXNG unreachable, skipping web search');
    return {
      rawText: '',
      sourceCount: 0,
      collectorId: 'web_search',
      error: 'SearXNG unreachable',
    };
  }
}

// ---------------------------------------------------------------------------
// Query builder
// ---------------------------------------------------------------------------

function buildSearchQuery(task: CollectionTask): string {
  const query = task.query as Record<string, unknown>;
  const parts: string[] = [];

  if (query.subject && typeof query.subject === 'string') parts.push(query.subject);
  if (Array.isArray(query.keywords)) parts.push(...(query.keywords as string[]));
  if (query.region && typeof query.region === 'string') parts.push(query.region);

  return parts.join(' ');
}

// ---------------------------------------------------------------------------
// Content extraction
// ---------------------------------------------------------------------------

function extractReadableText(html: string): string {
  return html
    .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
    .replace(/<nav[^>]*>[\s\S]*?<\/nav>/gi, '')
    .replace(/<footer[^>]*>[\s\S]*?<\/footer>/gi, '')
    .replace(/<header[^>]*>[\s\S]*?<\/header>/gi, '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#039;/gi, "'")
    .replace(/&[a-z]+;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}
